import * as S from "./standard-dialog.styles";
import {Formik, FormikHelpers, FormikProps, useFormikContext} from "formik";
import {DynamicForm} from "@enter/support/src/components";
import React, {MutableRefObject, useEffect, useRef, useState} from "react";

import * as Yup from 'yup';
import {ClaimGuarantorResponsibility} from "../../../api";
import {InputField, ISelectOption} from "../../base";
import {StandardDialogButtonGroup} from "./standard-dialog.styles";
import {fields} from "@enter/support/src/pages/home/claims/claim-details/timeline/item/resources/hl7-parser/dictionary";

export interface IStandardDialogProps {
    schema: Yup.Schema<any>,
    initialValues: any,
    handleSubmit(response: any): void,
    handleCancel(): void,
    fields: IStandardDialogField[]
}

export interface IStandardDialogField {
    prop: string,
    type?: string,
    component?: React.ComponentType,
    label: string,
    maxLength?: number,
    options?: ISelectOption[],
    eval?: string;
    hidden?: boolean;
    placeholder?: string;
    defaultValue?: string;
}


export interface IStandardDialogButtonBarProps {
    className?: string;
    changed: boolean;
    cancelLabel?: string;
    saveLabel?: string;
    saveNotice?: string;
    stopLabel?: string;
    onCancel?: () => void;
    onError?: (traceId?: string) => void;
    onSave?: () => void;
    onStop?: () => Promise<boolean>;
    useNotify?: boolean;
}

interface Props<T> {
    onChange: (value: T) => void
    value: T
}

export function FormikObserver<T>(props: Props<T>) {
    useEffect(() => {
        props.onChange(props.value)
    }, [Object.values(props.value).join(', ')])
    return null
}

FormikObserver.defaultProps = {
    onChange: () => null,
}

export const StandardDialog = (props: IStandardDialogProps) => {
    //Very simple boolean logic evaluator (need to beef it up, but works for now without using insecure eval)
    //I know the logic here is not good and still specific, but it works for the use case and gives us the start
    //to doing similar things in the future
    //This only supports == and not != and always evaluates to a boolean. It also does not deal with cascading changes.
    //The main use case here is assigning a boolean based on a choice in a dropdown
    const evalFields = (value: any): any => {
        if(!value) return;
        const newMergedFields = {};
        const newRenderedFields = [];
        for(let i=0; i < props.fields.length; i++) {
            const field = props.fields[i];
            if(field.eval) {
                const firstEq = field.eval.indexOf('=');
                const leftOp = field.eval.substring(0, firstEq);
                const evalExpression = field.eval.substring(firstEq+1, field.eval.length).replace("(", "").replace(")", "");
                const nextOperatorIndex = evalExpression.indexOf('!') > -1 ? evalExpression.indexOf('!') : evalExpression.indexOf('=');
                const leftCompOp = evalExpression.substring(0, nextOperatorIndex);
                const rightCompValue = evalExpression.substring(evalExpression.lastIndexOf('=') + 1, evalExpression.length).replace("'", '').replace("'", '');
                const isExpressionEq = evalExpression.indexOf('==') > -1;
                const evalBoolean = (isExpressionEq) ? (value[leftCompOp] === rightCompValue) : (value[leftCompOp] !== rightCompValue);
                field[leftOp] = evalBoolean;
                newRenderedFields.push(field);
            } else {
                newMergedFields[field.prop] = field;
                newRenderedFields.push(field);
            }
        }
        return newRenderedFields;
    }

    const [renderedFields, setRenderedFields] = useState(props.fields);

    const onSubmit = async (values: any, formik: FormikHelpers<any>) => {
        console.log("onSubmit called");

        //Make sure hidden fields are not included
        const revisedValues = {...values};
        for(let i=0; i < renderedFields.length; i++) {
            if(renderedFields[i].hidden && revisedValues[renderedFields[i].prop] !== undefined) {
                revisedValues[renderedFields[i].prop] = undefined;
            }
        }

        formik.setSubmitting(true);

        try {
            await props.handleSubmit(revisedValues);
        } finally {
            formik.setSubmitting(false);
        }
    };

    const evalOnChange = (value: any): void => {
        setRenderedFields(evalFields(value));
    }

    const renderedRefs:MutableRefObject<any>[] = renderedFields.map(item=>useRef(null));


    return (
        <S.DialogBody>
            <Formik initialValues={props.initialValues}
                    validationSchema={props.schema}
                    onSubmit={onSubmit}
                    validator={() => ({})}>
                {(formik: FormikProps<any>): React.ReactElement => (
                    <>
                        <FormikObserver value={formik.values} onChange={value => evalOnChange(value)} />
                        {
                            renderedFields.map((item, index) => {
                                const currentRef = renderedRefs[index];
                                return item.hidden ? <span key={item.prop}></span> : <S.InputRow key={item.prop}>
                                    <S.InputAmount>
                                        <S.Label>{item.label}</S.Label>
                                        <S.FormField
                                            name={item.prop}
                                            innerRef={currentRef}
                                            maxLength={item.maxLength ? item.maxLength : 100}
                                            component={item.component ? item.component : InputField}
                                            options={item.options}
                                            placeholder={item.placeholder}
                                            // defaultValue={item.defaultValue}
                                            type={item.type ? item.type : "text"}
                                        />
                                    </S.InputAmount>
                                </S.InputRow>
                            })
                        }
                        <S.StandardDialogButtonGroup
                            changed={true}
                            useNotify={false}
                            onCancel={props.handleCancel}
                            onSave={async ()=> {
                                console.log('values:', formik.values);
                                formik.validateForm().then((validationErrors) => {
                                    console.log('Validation Errors:', validationErrors);
                                });
                                console.log("onSaving...");
                                await formik.submitForm();
                            }}
                            saveLabel="Save"
                            saveNotice="Saved"
                        />
                    </>
                )}
            </Formik>
        </S.DialogBody>
    );
}